import React, { useEffect, useRef, useState } from "react";
import { Download, FileDown, FileSpreadsheet, Loader2, Mail, Pencil, Plus, Search, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { useLang } from "../i18n";
import { EmailReportButton } from "./EmailReportButton";
import DateInput from "./DateInput";

const ORGANS = ["Ejecutivo", "Legislativo", "Judicial", "Organismo Autónomo", "Otro"];
const EMPTY = { full_name: "", id_number: "", position: "", institution: "", state_organ: "Ejecutivo", start_date: "", end_date: "", current: true, observations: "" };

const fmtMoney = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? (v || "—") : `B/. ${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

export const isCargoAlert = (p) => !!p && /(?<!vice )(?<!vice-)\bministr[oa]\b|\bdiputad|\bdirector|\bjef/i.test(p);

export function PepPanamaView() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [cargoFilter, setCargoFilter] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const selectedRow = rows.find((r) => r.id === selected) || null;

  const load = async (query = q, cargo = cargoFilter) => {
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await api.get("/pep-panama", { params: { q: query, cargo } });
      setRows(data);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const qs = () => `q=${encodeURIComponent(q)}&cargo=${encodeURIComponent(cargoFilter)}`;

  const sendEmail = async () => {
    setEmailBusy(true);
    try {
      const { data } = await api.post("/pep-panama/export/email", { q, cargo: cargoFilter });
      toast.success(`${t("search.email_sent")} ${data.to}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <div className="ec-card p-6 space-y-4" data-testid="peppa-view">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            className="ec-input !pl-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder={t("pp.search_ph")}
            data-testid="peppa-view-search-input"
          />
        </div>
        <button onClick={() => load()} data-testid="peppa-view-search-button" className="px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-bold text-slate-700 hover:border-slate-500 transition-colors">
          {t("search.run")}
        </button>
        <select className="ec-input !w-44" value={cargoFilter} onChange={(e) => { setCargoFilter(e.target.value); load(q, e.target.value); }} data-testid="peppa-view-cargo-filter">
          <option value="">{t("pp.cargo_all")}</option>
          <option value="diputad">{t("pp.cargo_diputados")}</option>
          <option value="(?<!vice )(?<!vice-)\bministr[oa]\b">{t("pp.cargo_ministros")}</option>
          <option value="vice ?ministr">{t("pp.cargo_viceministros")}</option>
          <option value="director">{t("pp.cargo_directores")}</option>
          <option value="jef">{t("pp.cargo_jefes")}</option>
          <option value="administrador">{t("pp.cargo_administradores")}</option>
        </select>
        <button onClick={() => downloadFile(`/pep-panama/export/pdf?${qs()}`, "lista_pep_panama.pdf")} data-testid="peppa-export-pdf" className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-bold text-slate-700 hover:border-slate-500 transition-colors">
          <Download className="w-4 h-4" /> {t("search.pdf")}
        </button>
        <button onClick={() => downloadFile(`/pep-panama/export/excel?${qs()}`, "lista_pep_panama.xlsx")} data-testid="peppa-export-excel" className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-bold text-slate-700 hover:border-slate-500 transition-colors">
          <FileSpreadsheet className="w-4 h-4" /> {t("search.excel")}
        </button>
        <button onClick={sendEmail} disabled={emailBusy} data-testid="peppa-export-email" className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-bold text-slate-700 hover:border-slate-500 transition-colors disabled:opacity-60">
          {emailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} {t("search.send_email")}
        </button>
      </div>
      {selectedRow && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3" data-testid="peppa-selected-bar">
          <span className="text-[11px] font-extrabold px-2 py-0.5 rounded bg-red-600 text-white" data-testid="peppa-selected-badge">PEP PAN</span>
          <span className="text-sm font-bold text-slate-800 flex-1 min-w-[200px]">{selectedRow.full_name} — {selectedRow.position || "—"}</span>
          <button onClick={() => downloadFile(`/pep-panama/${selectedRow.id}/pdf`, `pep_pan_${selectedRow.id_number || selectedRow.id}.pdf`)} data-testid="peppa-record-pdf" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors">
            <Download className="w-4 h-4" /> {t("search.pdf")}
          </button>
          <EmailReportButton pdfPath={`/pep-panama/${selectedRow.id}/pdf`} filename={`pep_pan_${selectedRow.id_number || selectedRow.id}.pdf`} subject={`Ficha PEP Panamá — ${selectedRow.full_name}`} module="Lista PEP Panamá" summary={`${selectedRow.full_name} — ${selectedRow.position || ""}`} testid="peppa-record-email" variant="outline" className="!text-sm !px-3" />
          <button onClick={() => setSelected(null)} data-testid="peppa-clear-selection" className="text-slate-400 hover:text-slate-700" title={t("pp.clear_sel")}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="peppa-view-table">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="bg-[#16335F] text-white text-left">
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_name")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_id")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_position")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_institution")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_organ")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_period")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_budget")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_salary")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_obs")}</th>
            </tr>
          </thead>
          <tbody>
            {!searched ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400" data-testid="peppa-empty-prompt">{t("pp.empty_prompt")}</td></tr>
            ) : loading ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400" data-testid="peppa-view-empty">{t("pp.empty")}</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} onClick={() => setSelected(selected === r.id ? null : r.id)} className={`border-t border-slate-100 cursor-pointer ${selected === r.id ? "bg-red-50" : "hover:bg-slate-50"}`} data-testid={`peppa-view-row-${r.id}`}>
                <td className="px-3 py-2.5 font-semibold text-slate-800">{r.full_name}
                  {selected === r.id && <span className="ml-2 text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-red-600 text-white align-middle" data-testid={`peppa-peppan-badge-${r.id}`}>PEP PAN</span>}
                </td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{r.id_number || "—"}</td>
                <td className={`px-3 py-2.5 text-xs ${isCargoAlert(r.position) ? "text-red-600 font-bold" : "text-slate-600"}`} data-testid={`peppa-position-${r.id}`}>{r.position || "—"}</td>
                <td className="px-3 py-2.5 text-slate-600 text-xs">{r.institution || "—"}
                  {r.source_label && r.source_label !== r.institution && <div className="text-[10px] text-slate-400">{t("pp.badge")}: {r.source_label}</div>}</td>
                <td className="px-3 py-2.5 text-slate-600 text-xs">{r.state_organ || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600 font-mono">
                  {r.start_date || "—"} → {r.current ? <span className="font-bold text-emerald-700">{t("pp.current")}</span> : (r.end_date || "—")}
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600 font-mono">{r.budget_line || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600 font-mono" data-testid={`peppa-salary-${r.id}`}>{fmtMoney(r.salary)}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500 max-w-[180px] truncate" title={r.observations}>{r.observations || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PepPanamaList() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [cargoFilter, setCargoFilter] = useState("");
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importSource, setImportSource] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkScope, setBulkScope] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async (query = q, cargo = cargoFilter) => {
    setLoading(true);
    try {
      const { data } = await api.get("/pep-panama", { params: { q: query, cargo } });
      setRows(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(""); }, []);

  const openNew = () => { setEditId(null); setForm(EMPTY); setModal(true); };
  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      full_name: r.full_name || "", id_number: r.id_number || "", position: r.position || "",
      institution: r.institution || "", state_organ: r.state_organ || "Ejecutivo",
      start_date: r.start_date || "", end_date: r.end_date || "",
      current: !!r.current, observations: r.observations || "",
    });
    setModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.patch(`/pep-panama/${editId}`, form);
      else await api.post("/pep-panama", form);
      toast.success(t("pp.saved"));
      setModal(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async (r) => {
    if (!window.confirm(t("pp.confirm_delete"))) return;
    try {
      await api.delete(`/pep-panama/${r.id}`);
      toast.success(t("pp.deleted"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImportBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(rd.result);
        rd.onerror = rej;
        rd.readAsDataURL(f);
      });
      const { data } = await api.post("/pep-panama/import", { file_base64: b64, filename: f.name, source_label: importSource });
      toast.success(`${data.inserted} ${t("pp.imported")}`);
      load("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setImportBusy(false);
    }
  };

  const srcLabels = [...new Set(rows.map((r) => r.source_label).filter(Boolean))];

  const bulkDel = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/pep-panama/bulk-delete", { source_label: bulkScope, confirm: true });
      toast.success(`${data.deleted} ${t("pp.bulk_deleted")}`);
      setBulkOpen(false);
      setBulkScope("");
      load("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div className="ec-card p-6 space-y-4" data-testid="peppa-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            className="ec-input !pl-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder={t("pp.search_ph")}
            data-testid="peppa-search-input"
          />
        </div>
        <button onClick={() => load()} data-testid="peppa-search-button" className="px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-bold text-slate-700 hover:border-slate-500 transition-colors">
          {t("search.run")}
        </button>
        <select className="ec-input !w-44" value={cargoFilter} onChange={(e) => { setCargoFilter(e.target.value); load(q, e.target.value); }} data-testid="peppa-cargo-filter">
          <option value="">{t("pp.cargo_all")}</option>
          <option value="diputad">{t("pp.cargo_diputados")}</option>
          <option value="(?<!vice )(?<!vice-)\bministr[oa]\b">{t("pp.cargo_ministros")}</option>
          <option value="vice ?ministr">{t("pp.cargo_viceministros")}</option>
          <option value="director">{t("pp.cargo_directores")}</option>
          <option value="jef">{t("pp.cargo_jefes")}</option>
          <option value="administrador">{t("pp.cargo_administradores")}</option>
        </select>
        <button onClick={openNew} data-testid="peppa-add-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors">
          <Plus className="w-4 h-4" /> {t("pp.add")}
        </button>
        <input className="ec-input !w-64" value={importSource} onChange={(e) => setImportSource(e.target.value)} placeholder={t("pp.source_ph")} data-testid="peppa-import-source" />
        <button onClick={() => fileRef.current?.click()} disabled={importBusy} data-testid="peppa-import-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-[#044CBC] text-[#044CBC] text-sm font-bold hover:bg-blue-50 transition-colors disabled:opacity-60">
          {importBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {t("pp.import")}
        </button>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onFile} data-testid="peppa-import-file" />
        <button onClick={() => downloadFile("/pep-panama/template", "plantilla_lista_pep_panama.xlsx")} data-testid="peppa-template-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-bold text-slate-700 hover:border-slate-500 transition-colors">
          <FileDown className="w-4 h-4" /> {t("pp.template")}
        </button>
        <button onClick={() => { setBulkScope(""); setBulkOpen(true); }} data-testid="peppa-bulk-delete-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-red-300 bg-white text-sm font-bold text-red-600 hover:bg-red-50 transition-colors">
          <Trash2 className="w-4 h-4" /> {t("pp.bulk_delete")}
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="peppa-table">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-[#16335F] text-white text-left">
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_name")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_id")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_position")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_institution")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_organ")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_period")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_budget")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_salary")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pp.col_obs")}</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400" data-testid="peppa-empty">{t("pp.empty")}</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`peppa-row-${r.id}`}>
                <td className="px-3 py-2.5 font-semibold text-slate-800">{r.full_name}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{r.id_number || "—"}</td>
                <td className={`px-3 py-2.5 text-xs ${isCargoAlert(r.position) ? "text-red-600 font-bold" : "text-slate-600"}`} data-testid={`peppa-position-${r.id}`}>{r.position || "—"}</td>
                <td className="px-3 py-2.5 text-slate-600 text-xs">{r.institution || "—"}
                  {r.source_label && r.source_label !== r.institution && <div className="text-[10px] text-slate-400">{t("pp.badge")}: {r.source_label}</div>}</td>
                <td className="px-3 py-2.5 text-slate-600 text-xs">{r.state_organ || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600 font-mono">
                  {r.start_date || "—"} → {r.current ? <span className="font-bold text-emerald-700">{t("pp.current")}</span> : (r.end_date || "—")}
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600 font-mono">{r.budget_line || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600 font-mono" data-testid={`peppa-salary-${r.id}`}>{fmtMoney(r.salary)}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500 max-w-[180px] truncate" title={r.observations}>{r.observations || "—"}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(r)} data-testid={`peppa-edit-${r.id}`} className="text-[#044CBC] hover:text-[#033A8C] p-1" title={t("pp.edit")}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(r)} data-testid={`peppa-del-${r.id}`} className="text-red-500 hover:text-red-700 p-1" title={t("u.delete")}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {bulkOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="peppa-bulk-modal">
          <div className="ec-card p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-extrabold text-slate-900">{t("pp.bulk_delete")}</h2>
              <button type="button" onClick={() => setBulkOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="peppa-bulk-close"><X className="w-5 h-5" /></button>
            </div>
            <div>
              <label className="ec-label">{t("pp.bulk_scope")}</label>
              <select className="ec-input" value={bulkScope} onChange={(e) => setBulkScope(e.target.value)} data-testid="peppa-bulk-scope">
                <option value="">{t("pp.bulk_scope_all")}</option>
                {srcLabels.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="peppa-bulk-warning">{t("pp.bulk_warn")}</p>
            <div className="flex gap-3">
              <button onClick={bulkDel} disabled={bulkBusy} data-testid="peppa-bulk-confirm" className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60 flex items-center justify-center gap-2">
                {bulkBusy && <Loader2 className="w-4 h-4 animate-spin" />} {t("pp.bulk_confirm")}
              </button>
              <button onClick={() => setBulkOpen(false)} data-testid="peppa-bulk-cancel" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">
                {t("u.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="peppa-modal">
          <form onSubmit={save} className="ec-card p-6 w-full max-w-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-extrabold text-slate-900">{editId ? t("pp.edit") : t("pp.add")}</h2>
              <button type="button" onClick={() => setModal(false)} className="text-slate-400 hover:text-slate-700" data-testid="peppa-modal-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="ec-label">{t("pp.col_name")} *</label>
                <input className="ec-input" value={form.full_name} onChange={set("full_name")} required minLength={2} data-testid="peppa-f-name" />
              </div>
              <div>
                <label className="ec-label">{t("pp.col_id")}</label>
                <input className="ec-input" value={form.id_number} onChange={set("id_number")} data-testid="peppa-f-id" />
              </div>
              <div>
                <label className="ec-label">{t("pp.col_position")}</label>
                <input className="ec-input" value={form.position} onChange={set("position")} data-testid="peppa-f-position" />
              </div>
              <div>
                <label className="ec-label">{t("pp.col_institution")}</label>
                <input className="ec-input" value={form.institution} onChange={set("institution")} data-testid="peppa-f-institution" />
              </div>
              <div>
                <label className="ec-label">{t("pp.col_organ")}</label>
                <select className="ec-input" value={form.state_organ} onChange={set("state_organ")} data-testid="peppa-f-organ">
                  {ORGANS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="ec-label">{t("pp.f_start")}</label>
                <DateInput className="ec-input" value={form.start_date} onChange={set("start_date")} data-testid="peppa-f-start" />
              </div>
              <div>
                <label className="ec-label">{t("pp.f_end")}</label>
                <DateInput className="ec-input" value={form.end_date} onChange={set("end_date")} disabled={form.current} data-testid="peppa-f-end" />
              </div>
              <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.current} onChange={set("current")} className="w-4 h-4 accent-[#044CBC]" data-testid="peppa-f-current" />
                {t("pp.f_current")}
              </label>
              <div className="sm:col-span-2">
                <label className="ec-label">{t("pp.col_obs")}</label>
                <textarea className="ec-input" rows={2} value={form.observations} onChange={set("observations")} data-testid="peppa-f-obs" />
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={busy} data-testid="peppa-save-button" className="flex-1 px-4 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 flex items-center justify-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("u.save")}
              </button>
              <button type="button" onClick={() => setModal(false)} data-testid="peppa-cancel-button" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">
                {t("u.cancel")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
